import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { environment } from '../../environments/environment';

 const aiChatAPIUrl = (environment.name === 'production') 
  ? 'https://api.trial.teradata.com/ai-chat' 
  : `https://api.${environment.name}.trial.teradata.com/ai-chat`;

//const aiChatAPIUrl = 'https://ai-chat-service-561066095864.us-central1.run.app/api';


export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: Date;
}

export interface ChatHistoryResponse {
  session_id: string;
  history: ChatMessage[];
}

export interface ChatRequest {
  message: string;
  session_id?: string;
}

export interface ChatResponse {
  answer: string;
  source_documents: string[];
  session_id: string;
}

export interface HealthResponse {
  status: string;
  embeddings_loaded: boolean;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class AiChatService {
  private messages: ChatMessage[] = [];

  constructor(private http: HttpClient) { }

  /**
   * Send a chat message to the AI
   */
  public chat(message: string, sessionId?: string): Observable<ChatResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      return throwError(() => new Error('Not authenticated'));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    const body: ChatRequest = {
      message,
      session_id: sessionId
    };

    return this.http.post<ChatResponse>(`${aiChatAPIUrl}/chat`, body, { headers })
      .pipe(
        retry(1),
        catchError((error) => {
          if (error.status === 429) {
            return throwError(() => new Error('Rate limit exceeded. Please wait a moment.'));
          }
          if (error.status === 503) {
            return throwError(() => new Error('AI service is starting up. Please try again shortly.'));
          }
          return throwError(() => new Error(error.error?.error || 'Failed to send message'));
        })
      );
  }

  /**
   * Check health status of the AI chat service
   */
  public getHealth(): Observable<HealthResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      return throwError(() => new Error('Not authenticated'));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.get<HealthResponse>(`${aiChatAPIUrl}/health`, { headers });
  }

  /**
   * Get chat messages for local display
   */
  public getMessages(): ChatMessage[] {
    return this.messages;
  }

  /**
   * Add a message to the local chat history
   */
  public addMessage(role: 'user' | 'assistant', content: string): void {
    this.messages.push({
      role,
      content,
      timestamp: new Date()
    });
  }

  /**
   * Clear local chat history
   */
  public clearMessages(): void {
    this.messages = [];
  }

  /**
   * Get chat history for the current session
   */
  public getChatHistory(): Observable<ChatHistoryResponse> {
    const token = localStorage.getItem('token');
    if (!token) {
      return throwError(() => new Error('Not authenticated'));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.get<ChatHistoryResponse>(`${aiChatAPIUrl}/history`, { headers })
      .pipe(
        catchError((error) => {
          console.error('Failed to get chat history:', error);
          return throwError(() => new Error(error.error?.error || 'Failed to get chat history'));
        })
      );
  }

  /**
   * Delete chat history for the current session
   */
  public deleteChatHistory(): Observable<any> {
    const token = localStorage.getItem('token');
    if (!token) {
      return throwError(() => new Error('Not authenticated'));
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.delete(`${aiChatAPIUrl}/history`, { headers })
      .pipe(
        catchError((error) => {
          console.error('Failed to delete chat history:', error);
          return throwError(() => new Error(error.error?.error || 'Failed to delete chat history'));
        })
      );
  }
}
